import {
	IsArray,
	IsIn,
	IsNumber,
	IsObject,
	IsOptional,
	IsString,
	MaxLength,
} from 'class-validator';
import type {
	ViewLayoutConfiguration,
	ViewLayoutVisibility,
} from '../entity/view-layout-configuration.entity';

export const VIEW_LAYOUT_ENTITY_TYPES = [
	'client',
	'item',
	'vendor',
	'order',
	'invoice',
	'opportunity',
	'issue',
	'price_contract',
	'ownership',
	'purchase_order',
	// PD-17066 — a competitor's Overview tab.
	'competitor',
] as const;

export type ViewLayoutEntityType = (typeof VIEW_LAYOUT_ENTITY_TYPES)[number];

export const VIEW_LAYOUT_VIEW_TYPES = ['detail', 'card', 'sidebar'] as const;

export type ViewLayoutViewType = (typeof VIEW_LAYOUT_VIEW_TYPES)[number];

// Role reference for response
export interface RoleReference {
	id: number;
	name: string;
}

export class CreateViewLayoutDto {
	@IsString()
	@IsIn(VIEW_LAYOUT_ENTITY_TYPES)
	public entityType!: ViewLayoutEntityType;

	@IsString()
	@IsIn(VIEW_LAYOUT_VIEW_TYPES)
	@IsOptional()
	public viewType?: ViewLayoutViewType;

	@IsString()
	@MaxLength(128)
	public name!: string;

	@IsString()
	@IsIn(['organization', 'role'])
	public visibility!: ViewLayoutVisibility;

	@IsArray()
	@IsNumber({}, { each: true })
	@IsOptional()
	public roleIds?: Array<number>;

	@IsObject()
	public layout!: ViewLayoutConfiguration;
}

export class UpdateViewLayoutDto {
	@IsString()
	@MaxLength(128)
	@IsOptional()
	public name?: string;

	@IsString()
	@IsIn(['organization', 'role'])
	@IsOptional()
	public visibility?: ViewLayoutVisibility;

	@IsArray()
	@IsNumber({}, { each: true })
	@IsOptional()
	public roleIds?: Array<number>;

	@IsObject()
	@IsOptional()
	public layout?: ViewLayoutConfiguration;
}

export interface ViewLayoutDefinitionDto {
	id: number;
	entityType: string;
	viewType: string;
	name: string;
	visibility: ViewLayoutVisibility;
	sharedRoles: Array<RoleReference>;
	sortOrder: number;
	layout: ViewLayoutConfiguration;
	ownerUserId: number;
	createdDate: Date;
	modifiedDate: Date | null;
}

export type GetViewLayoutsResponseDto =
	| {
			success: true;
			data: Array<ViewLayoutDefinitionDto>;
	  }
	| {
			success: false;
			error: string;
	  };

export type GetViewLayoutResponseDto =
	| {
			success: true;
			data: ViewLayoutDefinitionDto;
	  }
	| {
			success: false;
			error: string;
	  };

export type CreateViewLayoutResponseDto =
	| {
			success: true;
			data: ViewLayoutDefinitionDto;
	  }
	| {
			success: false;
			error: string;
	  };

export type UpdateViewLayoutResponseDto =
	| {
			success: true;
			data: ViewLayoutDefinitionDto;
	  }
	| {
			success: false;
			error: string;
	  };

export type DeleteViewLayoutResponseDto =
	| {
			success: true;
			message: string;
	  }
	| {
			success: false;
			error: string;
	  };

export type ResolveViewLayoutResponseDto =
	| {
			success: true;
			data: ViewLayoutConfiguration | null;
			source: 'custom' | 'default';
	  }
	| {
			success: false;
			error: string;
	  };

export class ReorderViewLayoutsDto {
	@IsArray()
	@IsNumber({}, { each: true })
	public ids!: Array<number>;

	@IsString()
	@IsIn(VIEW_LAYOUT_ENTITY_TYPES)
	public entityType!: ViewLayoutEntityType;

	@IsString()
	@IsIn(VIEW_LAYOUT_VIEW_TYPES)
	@IsOptional()
	public viewType?: string;
}

export type ReorderViewLayoutsResponseDto =
	| {
			success: true;
	  }
	| {
			success: false;
			error: string;
	  };

// Re-export types from entity for convenience
export type {
	ViewFieldPlacement,
	ViewLayoutConfiguration,
	ViewLayoutVisibility,
	ViewSection,
} from '../entity/view-layout-configuration.entity';
